/* ==========================================================================
   PDP recommendations carousel

   Two jobs:
   1. When no manual picks are set, fetch Shopify's related-product
      recommendations and swap them in place of the skeleton.
   2. Wire the scroll-snap scroller to arrows and mobile dots.

   The carousel and hover also run the "Related merch" row
   (sections/product-upsell-blocks.liquid), which marks itself with
   data-pdp-carousel and reuses the same scroller markup.
   Collection pages (templates/collection.liquid) use the hover alone, via
   data-pdp-hover.

   No infinite-loop cloning here, unlike the film page. The PDP caps at a
   handful of cards, so cloning to fake a loop costs more than it earns and
   misbehaves at low counts. Arrows simply disable at the ends.
   ========================================================================== */

(function () {
  'use strict';

  // Both PDP sections include this file, so it can arrive twice. Run once.
  if (window.pdpCarouselLoaded) return;
  window.pdpCarouselLoaded = true;

  function initCarousel(root) {
    var scroller = root.querySelector('[data-pdp-recs-scroller]');
    var prevBtn = root.querySelector('[data-pdp-recs-prev]');
    var nextBtn = root.querySelector('[data-pdp-recs-next]');
    var dotsWrap = root.querySelector('[data-pdp-recs-dots]');

    if (!scroller) return;

    var items = Array.prototype.slice.call(
      scroller.querySelectorAll('.pdp-recs-item, .pdp-upsell-item, .pdp-stills-item')
    );
    var dots = dotsWrap
      ? Array.prototype.slice.call(dotsWrap.querySelectorAll('[data-pdp-recs-dot]'))
      : [];

    if (items.length === 0) return;

    // The index is tracked explicitly rather than derived from scrollLeft on
    // every click. Near the right-hand end the scroller clamps, so a derived
    // index would report the last card even when the view has only moved part
    // of a column, making the next Previous click jump more than one across.
    var index = 0;

    // Cards are sized in CSS from --pdp-carousel-width, falling back to 100vw.
    // 100vw includes the page scrollbar, so on desktop a row that should fit
    // exactly overhangs by the scrollbar's width. Measuring the scroller fixes
    // that, so five across really is five across.
    function measure() {
      root.style.setProperty('--pdp-carousel-width', scroller.clientWidth + 'px');
    }
    measure();

    // Scroll position that brings card i to the start of the row. Measured
    // from the first card, so it holds whether the row is inset with padding
    // (recommendations) or with margins (merch).
    function startOf(i) {
      return items[i].offsetLeft - items[0].offsetLeft;
    }

    function maxScroll() {
      return Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    }

    // Last card that can actually sit flush at the left edge. Beyond this the
    // scroller is already at its end, so stepping further would do nothing.
    //
    // Cards are sized from 100vw, which includes the page scrollbar, so the
    // true end usually sits a scrollbar's width past the last whole step.
    // Anything within a quarter of a card of the end counts as the end;
    // otherwise Next allows one extra click that only nudges the row ~15px.
    function maxIndex() {
      var limit = maxScroll();
      var slack = items[0].offsetWidth / 4;
      for (var i = 0; i < items.length; i++) {
        if (startOf(i) >= limit - slack) return i;
      }
      return items.length - 1;
    }

    // The last step goes all the way to the end, so the final card's right
    // edge isn't left clipped by that leftover scrollbar width.
    function offsetFor(i) {
      var target = items[i];
      if (!target) return 0;
      if (i >= maxIndex()) return maxScroll();
      return Math.min(startOf(i), maxScroll());
    }

    // Index nearest the current scroll position, used only to resync after a
    // manual scroll or swipe.
    function nearestIndex() {
      var left = scroller.scrollLeft;
      var closest = 0;
      var smallest = Infinity;
      items.forEach(function (item, i) {
        var distance = Math.abs(offsetFor(i) - left);
        if (distance < smallest) {
          smallest = distance;
          closest = i;
        }
      });
      return closest;
    }

    function syncControls() {
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
      });

      if (prevBtn) prevBtn.disabled = index <= 0;
      if (nextBtn) nextBtn.disabled = index >= maxIndex();

      // Everything fits, e.g. two merch blocks in a three-column row: hide
      // the arrows rather than showing both permanently greyed out.
      root.classList.toggle('pdp-carousel--static', maxIndex() === 0);
    }

    function goTo(next) {
      var clamped = Math.max(0, Math.min(next, maxIndex()));
      index = clamped;
      scroller.scrollTo({ left: offsetFor(clamped), behavior: 'smooth' });
      syncControls();
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goTo(index - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goTo(index + 1);
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function (event) {
        event.preventDefault();
        var target = parseInt(dot.getAttribute('data-pdp-recs-dot'), 10);
        if (!isNaN(target)) goTo(target);
      });
    });

    // Manual scrolling and swiping bypass goTo, so settle the tracked index
    // back onto whatever the user landed on.
    var settleTimer = null;
    scroller.addEventListener(
      'scroll',
      function () {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(function () {
          index = nearestIndex();
          syncControls();
        }, 120);
      },
      { passive: true }
    );

    window.addEventListener('resize', function () {
      measure();
      index = nearestIndex();
      syncControls();
    });

    syncControls();
  }

  // Card hover zoom, driven by GSAP (loaded globally in theme.liquid) rather
  // than a CSS transition. Each enter or leave replaces the running tween
  // (overwrite), starting from wherever the scale currently is, so moving in
  // and out quickly eases rather than snapping. force3D keeps the image on its
  // own GPU layer so it's never repainted mid-scale. Without GSAP the CSS
  // transition still applies.
  var HOVER_SCALE = 1.06;
  var MERCH_HOVER_SCALE = 1.07;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initHover(root) {
    if (typeof gsap === 'undefined' || reduceMotion) return;
    root.classList.add('pdp-recs--gsap');

    root.querySelectorAll('.pdp-card-link, .pdp-upsell-link').forEach(function (link) {
      var img = link.querySelector('.pdp-card-image img, .pdp-upsell-image img');
      if (!img || link.dataset.hoverBound === 'true') return;
      link.dataset.hoverBound = 'true';

      // Zoom from the centre. Cover frames don't clip (product-recommendations
      // .css), so the cover grows evenly a few pixels past its frame rather
      // than being cropped against an edge mid-zoom, which read as a jolt.
      gsap.set(img, { force3D: true, scale: 1, transformOrigin: '50% 50%' });

      function scaleTo(value) {
        gsap.to(img, { scale: value, duration: 0.45, ease: 'power2.out', overwrite: true });
      }

      // Merch images sit small in a padded frame, so they zoom further than
      // the DVD covers to read as clearly.
      var scale = link.classList.contains('pdp-upsell-link') ? MERCH_HOVER_SCALE : HOVER_SCALE;

      // Mouse only: touch fires enter without a matching leave, which would
      // leave the card stuck zoomed.
      link.addEventListener('pointerenter', function (event) {
        if (event.pointerType === 'mouse') scaleTo(scale);
      });
      link.addEventListener('pointerleave', function (event) {
        if (event.pointerType === 'mouse') scaleTo(1);
      });
    });
  }

  // "Related merch" quick-add buttons: the PDP Add to cart's calm hover (pink
  // fades in softly, arrow nudges up-right), plus feedback on add. Adding itself is application.js's
  // ajaxify (the form carries .js-ajax-add-form), which refreshes and opens
  // the mini-cart; "Added" shows once it opens.
  function initUpsellAdd(root) {
    var buttons = root.querySelectorAll('.pdp-upsell-add:not([disabled])');
    if (buttons.length === 0) return;

    var useGsap = typeof gsap !== 'undefined' && !reduceMotion;
    if (useGsap) root.classList.add('pdp-upsell--gsap');

    Array.prototype.forEach.call(buttons, function (button) {
      if (button.dataset.upsellAddBound === 'true') return;
      button.dataset.upsellAddBound = 'true';

      var fill = button.querySelector('.pdp-upsell-add-fill');
      var arrow = button.querySelector('.pdp-upsell-add-arrow');

      if (useGsap && fill) {
        gsap.set(fill, { scaleX: 1, opacity: 0 });

        button.addEventListener('pointerenter', function (event) {
          if (event.pointerType !== 'mouse') return;
          gsap.to(fill, { opacity: 1, duration: 0.2, ease: 'power1.out', overwrite: true });
          if (arrow) gsap.to(arrow, { x: 3, y: -3, duration: 0.35, ease: 'power2.out', overwrite: true });
        });

        button.addEventListener('pointerleave', function (event) {
          if (event.pointerType !== 'mouse') return;
          gsap.to(fill, { opacity: 0, duration: 0.2, ease: 'power1.out', overwrite: true });
          if (arrow) gsap.to(arrow, { x: 0, y: 0, duration: 0.3, ease: 'power2.out', overwrite: true });
        });
      }

      var form = button.closest('[data-pdp-upsell-form]');
      var label = button.querySelector('[data-pdp-upsell-add-label]');
      if (!form || !label) return;

      form.addEventListener('submit', function () {
        var original = 'Add to cart';
        label.textContent = 'Adding…';
        button.disabled = true;

        function restore(text, wait) {
          label.textContent = text;
          setTimeout(function () {
            label.textContent = original;
            button.disabled = false;
          }, wait);
        }

        // ajaxify adds the mini-cart-open class to <html> once the cart has
        // refreshed; that's the cue it worked.
        var html = document.documentElement;
        var watcher = new MutationObserver(function () {
          if (html.classList.contains('mini-cart-open')) {
            watcher.disconnect();
            clearTimeout(fallback);
            restore('Added ✓', 1800);
          }
        });
        watcher.observe(html, { attributes: true, attributeFilter: ['class'] });

        // If nothing happens (an error is alerted by ajaxify), reset anyway.
        var fallback = setTimeout(function () {
          watcher.disconnect();
          restore(original, 0);
        }, 6000);
      });
    });
  }

  // ?recs_debug=1 labels where the picks came from and, for automatic picks,
  // the score and what each film shares with this one. Off for customers.
  var DEBUG = /[?&]recs_debug=1\b/.test(window.location.search);

  var SOURCE_LABELS = {
    manual: 'Source: manual picks (Related products metafield)',
    auto: 'Source: automatic picks (snippets/pdp-auto-related.liquid)',
    api: "Source: Shopify's recommendations (no manual or automatic picks)",
    fetch: "Source: Shopify's recommendations (no manual or automatic picks)"
  };

  function showDebug(root) {
    if (!DEBUG) return;

    var source = root.getAttribute('data-pdp-recs-source');
    var heading = root.querySelector('.pdp-recs-heading');
    if (heading && !root.querySelector('.pdp-recs-debug-source')) {
      var note = document.createElement('p');
      note.className = 'pdp-recs-debug-source';
      note.textContent = SOURCE_LABELS[source] || 'Source: ' + source;
      (heading.closest('.pdp-carousel-header') || heading).insertAdjacentElement('afterend', note);
    }

    root.querySelectorAll('.pdp-recs-item').forEach(function (item) {
      if (item.querySelector('.pdp-recs-debug')) return;
      var label = document.createElement('p');
      label.className = 'pdp-recs-debug';

      var why = item.getAttribute('data-pdp-recs-why');
      if (why) {
        label.innerHTML = '<strong>Score ' + item.getAttribute('data-pdp-recs-score') + '</strong>';
        label.appendChild(document.createTextNode('Shares: ' + why.split(' + ').join(', ')));
      } else {
        label.textContent = source === 'manual' ? 'Manual pick' : 'Shopify recommendation';
      }
      item.appendChild(label);
    });
  }

  function fetchRecommendations(root) {
    var url = root.getAttribute('data-fetch-url');
    if (!url) return;

    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('Recommendations request failed');
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-pdp-recs]');

        // Nothing came back: remove the section rather than leaving a heading
        // above an empty strip.
        if (!fresh || fresh.querySelectorAll('.pdp-recs-item').length === 0) {
          if (DEBUG) console.info('[recs_debug] Shopify returned no recommendations, so the section was removed.');
          root.remove();
          return;
        }

        root.innerHTML = fresh.innerHTML;
        showDebug(root);
        initCarousel(root);
        initHover(root);
        if (window.pdpReveal) window.pdpReveal(root);
      })
      .catch(function () {
        root.remove();
      });
  }

  function init() {
    var carousels = document.querySelectorAll('[data-pdp-carousel]');
    Array.prototype.forEach.call(carousels, function (root) {
      if (root.dataset.recsBound === 'true') return;
      root.dataset.recsBound = 'true';
      initCarousel(root);
      initHover(root);
      initUpsellAdd(root);
    });

    // Card grids that only want the hover zoom, e.g. collection pages.
    var hoverOnly = document.querySelectorAll('[data-pdp-hover]');
    Array.prototype.forEach.call(hoverOnly, initHover);

    var roots = document.querySelectorAll('[data-pdp-recs]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.recsBound === 'true') return;
      root.dataset.recsBound = 'true';

      if (root.hasAttribute('data-fetch-url')) {
        fetchRecommendations(root);
      } else {
        showDebug(root);
        initCarousel(root);
        initHover(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
